/* eslint-disable react/jsx-props-no-spreading */
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { Box, Drawer, styled } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as Highcharts from "highcharts/highstock";
import { DateTime, type DateTime as DateTimeT } from "luxon";

import { MS_PER_DAY, MS_PER_HOUR } from "@config";
import type { UtfTheme } from "@config/utilifeed.theme";
import { ProductionDataPoint } from "@core";
import { logger as baseLogger } from "@core/logger";
import { DateRange } from "@core/utils/speedUtils";
import { useDebouncedValue } from "@hooks";

import { ChartNavigator } from "../ChartNavigator";
import QuickOptionButtonGroup from "../propai/shared/QuickOptionButtonGroup";
import { UtfDateRangePicker } from "../ui";
import withErrorBoundary from "../withErrorBoundary";
import {
  DEFAULT_QUICK_RANGES,
  DEFAULT_SPAN_TOLERANCE_DAYS,
  inferRangeLabel,
  nowWindowOf,
  QUICK_RANGE_CUSTOM,
  type QuickRange,
} from "./rangeInference";

export type { QuickRange };

const logger = baseLogger.getSubLogger({ name: "<FilterBar/>" });

const ROW_HEIGHT = 56;

type Extremes = { min: number; max: number };

/** Numeric hourly series — an alternative to `data` that skips the ISO round-trip. */
type NumericSeries = { startTime: number; data: number[] };

// Pure: returns the initial window + matching quick-range label for a given
// data range. Used both by the lazy `useState` initializer (so the first
// paint already shows the right window) and by the data-swap effect.
function computeDefaultWindow(
  rangeStartMillis: number | undefined,
  rangeEndMillis: number | undefined,
  quickRanges: QuickRange[] | undefined,
  defaultQuickRange: string | undefined
): { extremes: Extremes | null; timeRange: string } {
  if (rangeStartMillis == null || rangeEndMillis == null) {
    return { extremes: null, timeRange: QUICK_RANGE_CUSTOM };
  }
  if (quickRanges?.length && defaultQuickRange) {
    const match = quickRanges.find((r) => r.value === defaultQuickRange);
    // Apply the default quick range only when the data span can fill it; otherwise fall back to full range.
    // That is -  if the full range is less than the default range, show the full range and dont hight the default quick option
    const availableDays = (rangeEndMillis - rangeStartMillis) / MS_PER_DAY;
    if (match && availableDays >= match.days - DEFAULT_SPAN_TOLERANCE_DAYS) {
      const max = Math.min(rangeStartMillis + match.days * MS_PER_DAY, rangeEndMillis);
      return { extremes: { min: rangeStartMillis, max }, timeRange: defaultQuickRange };
    }
  }
  return {
    extremes: { min: rangeStartMillis, max: rangeEndMillis },
    timeRange: QUICK_RANGE_CUSTOM,
  };
}

const Row = styled("div")(({ theme }: { theme: UtfTheme }) => ({
  flex: 1,
  display: "flex",
  alignItems: "center",
  width: "100%",
  minHeight: ROW_HEIGHT,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  boxShadow: theme.shadows[1],
  gap: theme.spacing(2),
}));

const Section = styled(Box)(({ theme }: { theme: UtfTheme }) => ({
  display: "flex",
  width: "auto",
  height: "100%",
  alignItems: "center",
  color: theme.palette.grey["50"],
  gap: theme.spacing(1),
}));

const DrawerBody = styled("div")(({ theme }: { theme: UtfTheme }) => ({
  color: "white",
  display: "flex",
  overflow: "hidden",
  backgroundColor: theme.palette.primary.main,
}));

/**
 * Gets the time range from an array of production data points
 * @param data - Array of production data points
 * @param options.snapEndToDay - When true (default), the end snaps up to the
 *   final calendar day (`23:00`). Production follow-up data is day-aligned, so
 *   this keeps the navigator spanning whole days. Synthetic ranges that end on
 *   an exact hour (e.g. an Optimization `7×24h` run) pass `false` so the full
 *   range ends at the last data point instead of padding to end-of-day.
 * @returns Object containing start and end dates, or null values if data is empty
 */
export const getTimeRange = (
  data: ProductionDataPoint[],
  { snapEndToDay = true }: { snapEndToDay?: boolean } = {}
): DateRange => {
  if (!data?.length) return { start: null, end: null };

  const start = DateTime.fromISO(data[0].datetime);
  const lastPoint = DateTime.fromISO(data[data.length - 1].datetime);

  if (!start.isValid || !lastPoint.isValid) {
    throw new Error("Invalid datetime format in data points");
  }

  const end = snapEndToDay ? lastPoint.endOf("day").startOf("hour") : lastPoint;

  return {
    start,
    end,
  };
};

/**
 * Numeric-series equivalent of {@link getTimeRange}. Derives the range from an
 * hourly `{ startTime, data }` series directly in millis, so callers that already
 * hold numeric data don't have to serialise to ISO just for it to be parsed back.
 */
const getSeriesTimeRange = (
  series: NumericSeries,
  { snapEndToDay = true }: { snapEndToDay?: boolean } = {}
): DateRange => {
  if (!series.data.length) return { start: null, end: null };

  const start = DateTime.fromMillis(series.startTime);
  const lastPoint = DateTime.fromMillis(series.startTime + (series.data.length - 1) * MS_PER_HOUR);
  const end = snapEndToDay ? lastPoint.endOf("day").startOf("hour") : lastPoint;

  return { start, end };
};

// This bar is always open and only has one row, so it uses a single sizing block.
//
// Keep the height unitless. The browser ignores it, allowing the drawer to
// auto-size. A valid height makes it overlap the bottom PageToolbar.
function drawerSizing(theme: UtfTheme) {
  return {
    height: `calc(${ROW_HEIGHT} * 2 + 1px)`,
    transition: theme.transitions.create("height", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
  };
}

const FilterDrawer = ({ children, width = "100%", zIndex = 3, ...props }) => {
  const theme = useTheme<UtfTheme>();
  const paperDefaults = useMemo(
    () => ({
      width,
      boxSizing: "border-box",
      left: "initial",
    }),
    [width]
  );

  return (
    <Drawer
      variant="permanent"
      anchor="bottom"
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        position: "fixed",
        width: "100%",
        right: 0,
        zIndex,
        flexShrink: 0,
        ...drawerSizing(theme),
        "& .MuiDrawer-paper": {
          ...paperDefaults,
          ...drawerSizing(theme),
          "& > div": {
            ...drawerSizing(theme),
          },
        },
      }}
      {...props}
    >
      {children}
    </Drawer>
  );
};

type FilterBarProps = {
  /** Production data used to build the navigator and selected range. */
  data?: ProductionDataPoint[];
  dataColumn?: string;
  /**
   * Pre-built numeric hourly series for the bar.
   *
   * When provided, this is used instead of `data`/`dataColumn` and avoids
   * converting dates to ISO strings just to parse them again.
   */
  series?: NumericSeries;
  disabled: boolean;
  minDate?: DateTime | null;
  maxDate?: DateTime | null;
  onChange: (start: DateTimeT, end: DateTimeT) => void;
  onIntervalChange?: (nextInterval: string) => void;
  afterSetExtremes: (extremes: { min: number; max: number }) => void;
  chartOptions?: Highcharts.Options;
  width?: string;
  isLoading?: boolean;
  /**
   * Stacking order of the fixed bottom drawer. Defaults to `3`. Raise it (above
   * `theme.zIndex.appBar`) on screens that have a bottom `PageToolbar`, which
   * would otherwise cover the bar.
   */
  zIndex?: number;
  /**
   * Custom quick-range buttons. Each entry selects a window of `days` anchored
   * at the start of the data (use this for synthetic time ranges, e.g.
   * simulation results, where "Last day/week" relative to *now* is meaningless).
   * Overrides the default now-anchored quick options when provided.
   */
  quickRanges?: QuickRange[];
  /** `value` of the `quickRanges` entry to apply on initial data load. */
  defaultQuickRange?: string;
  /**
   * Whether the full range snaps its end up to the final calendar day.
   * Defaults to `true` (day-aligned follow-up data). Set `false` to
   *  end on an exact hour so the range ends at the last data point.
   */
  snapEndToDay?: boolean;
};

const FilterBarComponent: FC<FilterBarProps> = ({
  data,
  dataColumn,
  series,
  onChange,
  afterSetExtremes,
  width = "100%",
  chartOptions,
  isLoading = false,
  minDate,
  maxDate,
  zIndex,
  quickRanges,
  defaultQuickRange,
  snapEndToDay = true,
}) => {
  const navigatorChartRef = useRef();

  const range = useMemo(
    () =>
      series
        ? getSeriesTimeRange(series, { snapEndToDay })
        : getTimeRange(data || [], { snapEndToDay }),
    [series, data, snapEndToDay]
  );

  // Rows the navigator plots: either the numeric series (wrapped to `{ value }`)
  // or the production data points as-is.
  const navigatorData = useMemo(
    () => (series ? series.data.map((value) => ({ value })) : data),
    [series, data]
  );
  const navigatorColumn = series ? "value" : dataColumn;

  const rangeStartMillis = range.start?.toMillis();
  const rangeEndMillis = range.end?.toMillis();

  // One range config controls visibility, click behavior, and label inference.
  const effectiveRanges = useMemo(() => quickRanges ?? DEFAULT_QUICK_RANGES, [quickRanges]);

  // Window of `days` anchored at `rangeStartMillis`, clamped to the data end.
  const computeAnchoredWindow = useCallback(
    (days: number): [number, number] | null => {
      if (rangeStartMillis == null || rangeEndMillis == null) return null;
      const end = Math.min(rangeStartMillis + days * MS_PER_DAY, rangeEndMillis);
      return [rangeStartMillis, end];
    },
    [rangeStartMillis, rangeEndMillis]
  );

  const [extremes, setExtremes] = useState<Extremes | null>(
    () =>
      computeDefaultWindow(rangeStartMillis, rangeEndMillis, quickRanges, defaultQuickRange)
        .extremes
  );
  const [timeRange, setTimeRange] = useState<string>(
    () =>
      computeDefaultWindow(rangeStartMillis, rangeEndMillis, quickRanges, defaultQuickRange)
        .timeRange
  );

  // Quick-range availability:
  // - now-anchored ranges need enough history and data ending near now.
  // - start-anchored ranges need enough span to cover the range.
  const QUICK_DATE_OPTIONS = useMemo(() => {
    const availableDays =
      rangeStartMillis != null && rangeEndMillis != null
        ? (rangeEndMillis - rangeStartMillis) / MS_PER_DAY
        : 0;
    const now = DateTime.now();
    const endNearNow =
      !!range.end && range.end.isValid && Math.abs(range.end.diff(now, "days").as("days")) <= 1;

    return effectiveRanges.map((r) => {
      if ((r.anchor ?? "start") === "now") {
        const reachesBack =
          !!range.start &&
          range.start.isValid &&
          range.start.plus(nowWindowOf(r)).toMillis() <= now.toMillis();
        return { label: r.label, value: r.value, disabled: !endNearNow || !reachesBack };
      }
      return {
        label: r.label,
        value: r.value,
        disabled: availableDays < r.days - DEFAULT_SPAN_TOLERANCE_DAYS,
      };
    });
  }, [effectiveRanges, range.start, range.end, rangeStartMillis, rangeEndMillis]);

  // Reset to the default window only when the actual data range changes, not on prop-only re-renders.
  const lastDataRangeRef = useRef<{ start: number; end: number } | null>(null);
  if (lastDataRangeRef.current === null && rangeStartMillis != null && rangeEndMillis != null) {
    lastDataRangeRef.current = { start: rangeStartMillis, end: rangeEndMillis };
  }
  useEffect(() => {
    if (rangeStartMillis == null || rangeEndMillis == null) return;
    const last = lastDataRangeRef.current;
    if (last && last.start === rangeStartMillis && last.end === rangeEndMillis) return;
    lastDataRangeRef.current = { start: rangeStartMillis, end: rangeEndMillis };
    const next = computeDefaultWindow(
      rangeStartMillis,
      rangeEndMillis,
      quickRanges,
      defaultQuickRange
    );
    setExtremes(next.extremes);
    setTimeRange(next.timeRange);
  }, [rangeStartMillis, rangeEndMillis, quickRanges, defaultQuickRange]);

  const debouncedExtremes = useDebouncedValue(extremes, 100);

  useEffect(() => {
    if (afterSetExtremes && debouncedExtremes) afterSetExtremes(debouncedExtremes);
  }, [debouncedExtremes, afterSetExtremes]);

  // Only update the active quick range for user-driven navigator/scrollbar
  // changes. Programmatic setExtremes calls have no trigger and should not
  // overwrite the current quick-button/date-picker highlight.
  const handleAfterSetExtremes = useCallback(
    ({ min, max, trigger }: Extremes & { trigger?: string }) => {
      setExtremes({ min, max });
      if (!onChange) return;
      const start = DateTime.fromMillis(min);
      const end = DateTime.fromMillis(max);
      onChange(start, end);
      // Ignore programmatic setExtremes echoes so they do not overwrite the active range highlight.
      if (!trigger) return;
      setTimeRange(inferRangeLabel(start, end, effectiveRanges, { dataStart: range.start }));
    },
    [onChange, effectiveRanges, range.start]
  );

  // Push extremes changes (from datepicker or quick buttons) into the navigator chart.
  useEffect(() => {
    if (!extremes || extremes.min >= extremes.max) return;
    const chart = (navigatorChartRef.current as { chart?: Highcharts.Chart } | null)?.chart;
    chart?.xAxis?.[0]?.setExtremes(extremes.min, extremes.max);
  }, [extremes]);

  // Memoize custom chart options
  const customChartOptions = useMemo(
    () => ({
      ...chartOptions,
      plotOptions: {
        ...chartOptions?.plotOptions,
        series: {
          ...chartOptions?.plotOptions?.series,
          animation: false,
          turboThreshold: 5000,
        },
      },
    }),
    [chartOptions]
  );

  const handleDateRangeChange = useCallback(
    (start: DateTime, end: DateTime) => {
      if (!start && !end) return;

      onChange(start, end);
      setExtremes({ min: start.toMillis(), max: end.toMillis() });
      setTimeRange(inferRangeLabel(start, end, effectiveRanges, { dataStart: range.start }));
    },
    [onChange, effectiveRanges, range.start]
  );

  const handleTimeRangeChange = (newTimeRange: string) => {
    setTimeRange(newTimeRange);

    const match = effectiveRanges.find((r) => r.value === newTimeRange);
    if (!match) return;

    if ((match.anchor ?? "start") === "now") {
      // Now-anchored: a calendar window ending at the current time.
      const now = DateTime.now();
      const start = now.minus(nowWindowOf(match));
      onChange(start, now);
      setExtremes({ min: start.toMillis(), max: now.toMillis() });
      return;
    }

    // Start-anchored: a `days` window pinned at the data start, clamped to its end.
    const window = computeAnchoredWindow(match.days);
    if (!window) return;
    const [min, max] = window;
    onChange(DateTime.fromMillis(min), DateTime.fromMillis(max));
    setExtremes({ min, max });
  };

  if (!range.start || !range.end) {
    logger.error("FilterBar: range.start or range.end is missing");
    return null;
  }

  logger.trace("RENDER %j", { range, extremes });

  return (
    <FilterDrawer width={width} zIndex={zIndex} data-testid="floating-filterbar">
      <DrawerBody>
        <Box flex={1}>
          <Row
            sx={{ display: "flex", alignItems: "center", width: "100%" }}
            data-testid="floating-filterbar-hideable"
          >
            <Section>
              <QuickOptionButtonGroup
                ariaLabel="Time range quick options"
                options={QUICK_DATE_OPTIONS}
                activeValue={timeRange}
                onChange={(val) => handleTimeRangeChange(val.value)}
                sx={{
                  color: (theme) => theme.palette.background.paper,
                  borderColor: (theme) => theme.palette.background.paper,
                }}
              />
            </Section>
            <Section sx={{ flex: 1 }}>
              {navigatorData && navigatorColumn && !isLoading && (
                <ChartNavigator
                  data={navigatorData}
                  dataColumn={navigatorColumn}
                  range={range}
                  chartRef={navigatorChartRef}
                  afterSetExtremes={handleAfterSetExtremes}
                  chartOptions={customChartOptions}
                />
              )}
            </Section>
            <Section>
              {extremes && (
                <UtfDateRangePicker
                  size="small"
                  data-testid="date-range-picker"
                  inputVariant="outlined"
                  startDate={DateTime.fromMillis(extremes.min)}
                  endDate={DateTime.fromMillis(extremes.max)}
                  minDate={minDate}
                  maxDate={maxDate ?? DateTime.now()}
                  onChange={handleDateRangeChange}
                  onDark
                />
              )}
            </Section>
          </Row>
        </Box>
      </DrawerBody>
    </FilterDrawer>
  );
};

FilterBarComponent.displayName = "Floating.FilterBar";

export const FilterBar = withErrorBoundary<FilterBarProps>(FilterBarComponent);
